import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Icons, ProfanityMessage } from '@/constants/defaults';
import { screenSize } from '@/theme';
import { Icon, Button, Text, TextInput } from '@/ui-kits';
import { AccordianLayout } from '../AccordianLayout';
import { useDispatch, useSelector } from 'react-redux';
import {
  analyzeHealthReportRequest,
  resetPatientDashboardMessage,
  clearE10080Data,
  addConsultationReportRequest,
  removeConsultationReportRequest,
  removeRejectedFiles,
  proceedWithAiRecommendedSpecialistRequest,
  moveToNextStepOnProgressBarComplete,
  addConsultationQueryRequest,
} from '@/services/redux/slices';
import { setExistingFileIds } from '@/services/redux/slices/patientDashboardSlice';
import { useNavigate } from 'react-router-dom';
import { Routes } from '@/navigation/Routes';
import {
  patientDocumentAnalysisLoadingStatus,
  patientDashboardFailureState,
  patientDashboardConsultation,
  patientDashboardSpecialists,
  patientDashboardCategory,
  patientDashboardSuccessState,
  patientDashboardFiles,
  patientDashboardPaymentStatus,
  patientDashboardRejectedFiles,
  patientDashboardConsultationType,
  patientDashboardLinkedCase,
  patientDashboardLinkedCaseCategory,
  patientDashboardCurrentStep,
  patientDashboardReportLoading,
  accountDeletionStatusSelector,
  patientDashboardConsultationFlow,
  patientDashboardHospitalName,
  patientDashboardManualCategorySelectionFlag,
  patientDashboardPreviousFiles,
  allAppSpecializations,
  patientDashboardPatientQuery,
} from '@/services/redux/selectors';
import { cn } from '@/utils/common_functions';
import { ErrorCodes, SuccessCodes } from '@/utils/getMessages';
import { CategoryMismatchModal } from '../CategoryMismatchModal';
import { DuplicateFilesModal } from '../DuplicateFilesModal';
import { InvalidFilesModal } from '../InvalidFilesModal';
import { RecentCasesModal, RecentCaseFile } from './RecentCasesModal';
import { Doctor, FileSerializableItem, RejectedFile, AnalyzeHealthReportRequestPayload } from '@/interface';
import { FilePreview } from '../../../common';
import { ConsulationFlowType, ConsultationType, PatientAccountTicketRequestStatus, ProgressBarSteps } from '@/constants';
import { ProgressBar } from '@/components/common/progressBar';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { isEmpty } from 'ramda';
import { useWindowDimensions } from '@/hooks';
import { hasBadWords } from '@/utils/profanity';
import { recentCasesSelector, patientDashboardExistingFileIds } from '@/services/redux/selectors/patientDashboardSelector';

const supportedExtentions = ['.png', '.jpeg', '.jpg', '.pdf', '.docx', '.doc'];

const formatClosedAt = (value?: string | null): string => {
  if (!value) return '--';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(value));
};

interface UploadReportProps {
  open: boolean;
  success: boolean;
  selectedSpecialistName: string;
}
export const UploadReport: React.FC<UploadReportProps> = props => {
    const navigate = useNavigate();
  // ===========================
  // props
  // ===========================
  const { open, success, selectedSpecialistName } = props;
  // ===========================
  // hooks
  // ===========================
  const dispatch = useDispatch();
  const { width } = useWindowDimensions();

  // ===========================
  // selectors
  // ===========================
  const isLoading = useSelector(patientDocumentAnalysisLoadingStatus);
  const failureCode = useSelector(patientDashboardFailureState) as ErrorCodes;
  const aiCategory = useSelector(patientDashboardCategory);
  const files = useSelector(patientDashboardFiles);
  const successCode = useSelector(patientDashboardSuccessState) as SuccessCodes;
  const specializations = useSelector(allAppSpecializations);
  const consultation_id = useSelector(patientDashboardConsultation);
  const aiRecommendedSpecialists = useSelector(patientDashboardSpecialists);
  const paymentStatus = useSelector(patientDashboardPaymentStatus);
  const currentStep = useSelector(patientDashboardCurrentStep);
  const rejectedFiles = useSelector(patientDashboardRejectedFiles);
  const consultation_type = useSelector(patientDashboardConsultationType);
  const linked_case = useSelector(patientDashboardLinkedCase);
  const linked_category = useSelector(patientDashboardLinkedCaseCategory);
  const addReportLoading = useSelector(patientDashboardReportLoading);
  const accountDeletionStatus = useSelector(accountDeletionStatusSelector);
  const consultationFlow = useSelector(patientDashboardConsultationFlow);
  const hospitalName = useSelector(patientDashboardHospitalName);
  const isManualCategorySelectionFlag = useSelector(patientDashboardManualCategorySelectionFlag);
  const previousFile = useSelector(patientDashboardPreviousFiles);
  const patientQuery = useSelector(patientDashboardPatientQuery);
  const recentCases = useSelector(recentCasesSelector);
  const specialistRecentFiles = useSelector((state: any) => state.patientDashboard.dashboardState.specialist_recent_files ?? []);
  const existingFileIds = useSelector(patientDashboardExistingFileIds);

  // ===========================
  // states
  // ===========================
  // Get AI-recommended category name from category ID
  const linked_category_label = useMemo(() => specializations.find(spec => spec.id === linked_category)?.label, [linked_category]);
  const aiCategoryName = useMemo(() => {
    if (!aiCategory) return undefined;
    const category = specializations.find(spec => spec.id === aiCategory);
    return category?.label;
  }, [aiCategory, specializations]);

  // Error caching for E-10080
  const [cachedError, setCachedError] = useState<{
    errorCode: string;
    fileHashes: Set<string>;
    consultation_id?: number | null;
    aiRecommendedSpecialists?: Doctor[];
    aiCategory?: number | null;
  } | null>(null);

  const [showCategoryMismatch, setShowCategoryMismatch] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showInvalidModal, setShowInvalidModal] = useState(false);
  const [patientQueryInput, setPatientQueryInput] = useState(patientQuery ?? '');
  const [isPatientQueryEditing, setIsPatientQueryEditing] = useState(false);
  const [duplicateFileNames, setDuplicateFileNames] = useState<string[]>([]);
  const [invalidFileNames, setInvalidFileNames] = useState<Record<string, RejectedFile>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string>('');
  const [previousFileOpen, setPreviousFileOpen] = useState<boolean>(false);
  const [showRecentCasesModal, setShowRecentCasesModal] = useState(false);
  const loadingIds = new Set(Object.keys(addReportLoading));
  const visibleFiles = files ? files.filter((f: FileSerializableItem) => {
    if (loadingIds.has(f.id.toString()) && typeof addReportLoading[f.id.toString()] === 'object') {
      return false;
    }
    return true;
  }) : [];
  const disabledAction = useMemo(() => {
    return (
      (typeof isLoading === 'number' && isLoading !== ProgressBarSteps.NotStarted) ||
      paymentStatus === 'success' ||
      accountDeletionStatus === PatientAccountTicketRequestStatus.Approved
    );
  }, [isLoading, paymentStatus, accountDeletionStatus]);
  
  /**
   * utility state to get whether the report is being analyzed
   */
  const reportAnalyzing = useMemo(() => {
    return typeof isLoading === 'number' && isLoading !== ProgressBarSteps.NotStarted;
  }, [isLoading]);

  /**
   * utility state to get whether the patient query is in edit mode
   */
  const isPatientQueryEditMode = useMemo(() => {
    if (reportAnalyzing) return true;
    return !patientQuery || isPatientQueryEditing;
  }, [patientQuery, isPatientQueryEditing, reportAnalyzing]);

  /**
   * utility state to get whether the patient query is disabled
   */
  const patientQueryEditingDisabled = useMemo(() => {
    if (currentStep > 1) return true;
    if (paymentStatus === 'success') return true;
    return currentStep === 1 && reportAnalyzing;
  }, [currentStep, reportAnalyzing]);

  // ===========================
  // functions
  // ===========================
  /**
   * Deletes file from local state, Redux, and localStorage
   * Prevents deleted pre-signup files from reappearing on refresh
   */
  const handleProgressBarComplete = () => {
    setTimeout(() => {
      dispatch(moveToNextStepOnProgressBarComplete());
    }, 1000);
  };
  const handleDelete = (id: number) => {
    if (!consultation_id) return;
    
    const deletedFile = selectedPreviousFiles?.find((f: FileSerializableItem) => f.id === id);
    if (deletedFile) {
      const updatedIds = (existingFileIds || []).filter(fileId => fileId !== id);
      dispatch(setExistingFileIds(updatedIds));
    }
    
    dispatch(
      removeConsultationReportRequest({
        id: id,
      }),
    );
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const payload = new FormData();
    const selectedFiles = event.target.files ?? [];

    // add the payload
    for (const file of selectedFiles) {
      payload.append('files', file);
    }

    // call the api
    dispatch(addConsultationReportRequest(payload));

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleTogglePreviousFiles = () => {
    setShowRecentCasesModal(true);
  };

  const handleAddSelectedPreviousFiles = (filesToAdd: RecentCaseFile[]) => {
    const fileIds = filesToAdd.map(f => f.id);
    dispatch(setExistingFileIds(fileIds));
    const formData = new FormData();
    formData.append('existing_file_ids', JSON.stringify(fileIds));
    dispatch(addConsultationReportRequest(formData));
  };
  // Flow 2: Clear existing file IDs when mode switches
  const currentMode = useMemo(() => {
    return (consultationFlow === ConsulationFlowType.PreSelectedSpecialist || consultation_type === ConsultationType['Follow Up Second Opinion'] || consultation_type === ConsultationType['Referral Second Opinion'] || consultation_type === ConsultationType['Hospital Follow Up Second Opinion'] || consultation_type === ConsultationType['Hospital Referral Second Opinion']) ? 'reuse' : 'select';
  }, [consultationFlow, consultation_type]);
  
  const [prevModeFilter, setPrevModeFilter] = useState(currentMode);
  
  useEffect(() => {
    if (currentMode !== prevModeFilter) {
      dispatch(setExistingFileIds([]));
      setPrevModeFilter(currentMode);
    }
  }, [currentMode, prevModeFilter, dispatch]);
  
  // Redirect to dashboard only after successful addConsultationReport
  React.useEffect(() => {
    if (successCode === 'S-10021' || successCode === 'S-10020' || successCode === 'S-10101') {
      navigate(Routes.DASHBOARD);
    }
  }, [successCode, navigate]);

  const handleView = (file: FileSerializableItem) => {
    if (file.url) window.open(file.url, '_blank');
  };

  const selectedPreviousFiles = useMemo(() => {
    return previousFile;
  }, [previousFile]);

  // Flow 1: Get already-added file IDs from previously shared files
  const alreadyAddedFileIds = useMemo(() => {
    return (selectedPreviousFiles || []).map((f: FileSerializableItem) => f.id);
  }, [selectedPreviousFiles]);

  const handleSubmit = () => {
    if (cachedError?.errorCode === 'E-10080') {
      setShowCategoryMismatch(true);
      return;
    }

    if (!consultation_id) return;

    const payload: AnalyzeHealthReportRequestPayload = {
      id: consultation_id,
    };
    dispatch(analyzeHealthReportRequest(payload));
  };

  const handleProceedWithAI = () => {
    if (!consultation_id) return;
    dispatch(proceedWithAiRecommendedSpecialistRequest({ id: consultation_id }));
  };

  const handleCloseMismatchModal = () => {
    setShowCategoryMismatch(false);
    setCachedError(null);
    dispatch(clearE10080Data());
  };

  const handleSubmitPatientQuery = () => {
    if (hasBadWords(patientQueryInput)){
      setError(ProfanityMessage);
      return;
    }else{
      setError('')
    }
    dispatch(
      addConsultationQueryRequest({
        ...(consultation_id != null && { id: consultation_id }),
        query: patientQueryInput,
      }),
    );
  };

  // ===========================
  // side effects
  // ===========================
  // E-10080: Cache error to prevent redundant API calls on modal reopen
  // E-10081: Show duplicate files modal when API fails with duplicate files error
  useEffect(() => {
    if (successCode) {
      if (successCode === 'S-10101'){
        setShowCategoryMismatch(false);
        setCachedError(null);
      }
      if (successCode === 'S-10120') {
        setIsPatientQueryEditing(false);
      }
    }
    if (failureCode) {
      if (failureCode === 'E-10080' && files.length > 0) {
        const fileHashes = new Set(files.map((f: FileSerializableItem) => f.name + ':' + f.size.toString()));
        setCachedError({
          errorCode: 'E-10080',
          fileHashes,
          consultation_id: consultation_id,
          aiRecommendedSpecialists: aiRecommendedSpecialists,
          aiCategory: aiCategory,
        });
        setShowCategoryMismatch(true);
      } else if (failureCode === 'E-10081' && files.length > 0) {
        // E-10081: Duplicate files detected
        // Detect duplicate file names from uploaded files
        const fileNameCounts = new Map<string, number>();
        for (const f of files) {
          const fileName = f.name.toLowerCase();
          fileNameCounts.set(fileName, (fileNameCounts.get(fileName) || 0) + 1);
        }

        // Get file names that appear more than once (duplicates)
        const duplicateNames: string[] = [];
        for (const f of files) {
          const fileName = f.name.toLowerCase();
          if ((fileNameCounts.get(fileName) || 0) > 1) {
            // Only add if not already in the list (preserve original case)
            if (!duplicateNames.includes(f.name)) {
              duplicateNames.push(f.name);
            }
          }
        }

        setDuplicateFileNames(duplicateNames);
        setShowDuplicateModal(true);
        // Reset the error to prevent toast from showing
        dispatch(resetPatientDashboardMessage());
      }
    }
  }, [failureCode, successCode, files, consultation_id, aiRecommendedSpecialists, aiCategory, dispatch]);

  // Keep local input in sync with Redux when patientQuery is loaded or after save (only when not editing)
  useEffect(() => {
    if (patientQuery != null) {
      setPatientQueryInput(patientQuery);
    }
  }, [patientQuery]);

  /**
   * Show invalid files modal when rejected_files are present in the response
   * Only show if we actually have rejected files from the API response
   */
  useEffect(() => {
    // Only process if rejectedFiles exists and has items
    if (rejectedFiles && rejectedFiles.length > 0) {
      // Some back-end validations may return duplicates under rejected_files.
      // Treat those as "duplicate" instead of "invalid".
      const duplicateRejected = rejectedFiles.filter(
        rf => rf.status_code === 'E-10081' || /duplicate/i.test(rf.message || ''),
      );
      const invalidRejected = rejectedFiles.filter(
        rf => !(rf.status_code === 'E-10081' || /duplicate/i.test(rf.message || '')),
      );

      if (duplicateRejected.length > 0) {
        const names = Array.from(new Set(duplicateRejected.map(r => r.filename)));
        setDuplicateFileNames(names);
        setShowDuplicateModal(true);
      }

      if (invalidRejected.length > 0) {
        setInvalidFileNames(
          invalidRejected.reduce((acc, rejectFile) => ({ ...acc, [rejectFile.filename]: rejectFile }), {}),
        );
        setShowInvalidModal(true);
      } else {
        setShowInvalidModal(false);
      }
    }
  }, [rejectedFiles]);

  useEffect(() => {
    if (cachedError && files?.length > 0) {
      const currentFileHashes = new Set(files?.map((f: any) => f.hash));
      const filesChanged = currentFileHashes.size !== cachedError.fileHashes.size || ![...currentFileHashes].every(hash => cachedError.fileHashes.has(hash));

      if (filesChanged) {
        setCachedError(null);
        dispatch(resetPatientDashboardMessage());
      }
    }
  }, [files, cachedError, dispatch]);

  // when we clear the patient query input, save it as empty string in backend
  useEffect(() => {
    if (patientQueryInput === "" && patientQuery) {
      dispatch(
        addConsultationQueryRequest({
          ...(consultation_id != null && { id: consultation_id }),
          query: patientQueryInput,
        }),
      );
    }
  }, [patientQueryInput, patientQuery]);

  return (
    <AccordianLayout icon="circle_1" title="Upload Report" open={open} key="Upload Report" success={success}>
      {/* Collapsible Content */}
      <div className="px-4 sm:px-6 pb-6 sm:pb-8 flex flex-col">
        {/* WARNING MESSAGE - Only shows when coming from specialist page */}
        {!isManualCategorySelectionFlag && (
          <>
            {consultationFlow === ConsulationFlowType.PreSelectedSpecialist && consultation_type !== ConsultationType['Follow Up Second Opinion'] && consultation_type !== ConsultationType['Referral Second Opinion'] && consultation_type !== ConsultationType['Hospital Follow Up Second Opinion'] && consultation_type !== ConsultationType['Hospital Referral Second Opinion'] && (
              aiRecommendedSpecialists[0]?.close_at ? (
                <WarningMessage>
                  Important: You are about to begin a consultation with{' '}
                  <span className="font-FigtreeSemiBold">{aiRecommendedSpecialists[0]?.name}</span>{' '}
                  (<span className="font-FigtreeSemiBold">{aiRecommendedSpecialists[0]?.specialization?.label}</span>),
                  whom you previously consulted on{' '}
                  <span className="font-FigtreeSemiBold">{formatClosedAt(aiRecommendedSpecialists[0]?.close_at)}</span>.
                  Please upload only relevant {aiRecommendedSpecialists[0]?.specialization?.label} documents.
                </WarningMessage>
              ) : (
                <WarningMessage>
                  Important: You've selected a <span className="font-FigtreeSemiBold">{aiCategoryName}</span> specialist for your consultation. Please ensure all
                  uploaded documents are specifically related to {aiCategoryName} only
                </WarningMessage>
              )
            )}
            {/* Hospital Assigned Warning - Only shows when hospital assigns specialist */}
            {consultationFlow === ConsulationFlowType.HospitalSpecialistAssigned && (
              <WarningMessage>
                We’ll assign an available <span className="font-FigtreeSemiBold">{aiCategoryName}</span> specialist from{' '}
                <span className="font-FigtreeSemiBold">{hospitalName}</span>. Please upload documents related to{' '}
                <span className="font-FigtreeSemiBold">{aiCategoryName}</span> only.
              </WarningMessage>
            )}
            {/* Followup Warning - Only shows when follow up case it there */}
            {(consultation_type === ConsultationType['Follow Up Second Opinion'] || consultation_type === ConsultationType['Hospital Follow Up Second Opinion']) && linked_case && (
              <WarningMessage>
                This is a follow-up consultation with a <span className="font-FigtreeSemiBold">{linked_category_label}</span> specialist for Case ID:{' '}
                <span className="font-FigtreeSemiBold">#{linked_case.id.toString()}</span>. Please ensure all uploaded documents are specifically related to{' '}
                {linked_category_label}.
              </WarningMessage>
            )}

            {(consultation_type === ConsultationType['Referral Second Opinion'] || consultation_type === ConsultationType['Hospital Referral Second Opinion']) && linked_case && (
              <WarningMessage>
                You have been referred to <span className="font-FigtreeSemiBold">{linked_category_label}</span> specialist for Case ID:{' '}
                <span className="font-FigtreeSemiBold">#{linked_case.id.toString()}</span>. Please ensure all uploaded documents are specifically related to{' '}
                {linked_category_label}.
              </WarningMessage>
            )}

            {consultationFlow === ConsulationFlowType.HospitalSpecialistPreSelected && (
              <WarningMessage>
                You’re consulting a <span className="font-FigtreeSemiBold">{aiCategoryName}</span> specialist from{' '}
                <span className="font-FigtreeSemiBold">{hospitalName}</span>. Please ensure all uploaded documents are specifically related to{' '}
                <span className="font-FigtreeSemiBold">{aiCategoryName}</span>.
              </WarningMessage>
            )}
          </>
        )}

        {/* Upload Box */}
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 sm:p-6 flex flex-col items-center text-center mt-6 overflow-hidden">
          <img src={Icons.upload_report} alt="Upload Report" className="w-12 h-12 mb-3" />
          <p className="text-ui_input_border_dark text-xs font-FigtreeRegular mb-2 whitespace-pre-line">
            {'Please upload documents for one medical condition at a time. \nYou can select multiple files together.'}
          </p>
          <p className="text-xs text-ui_input_border_dark mt-6 mb-1 font-FigtreeSemiBold">Supported formats: JPG, PNG, PDF</p>
          <input type="file" ref={fileInputRef} multiple accept={supportedExtentions.join(',')} className="hidden" onChange={handleFileChange} />

          {(() => {
            const isReuse =
              consultationFlow === ConsulationFlowType.PreSelectedSpecialist ||
              consultationFlow === ConsulationFlowType.HospitalSpecialistAssigned ||
              consultationFlow === ConsulationFlowType.HospitalSpecialistPreSelected ||
              consultation_type === ConsultationType['Follow Up Second Opinion'] ||
              consultation_type === ConsultationType['Referral Second Opinion'] ||
              consultation_type === ConsultationType['Hospital Follow Up Second Opinion'] ||
              consultation_type === ConsultationType['Hospital Referral Second Opinion'];

            return (
              <div className="mt-4 flex w-full max-w-[560px] flex-col gap-3 sm:flex-row sm:justify-center">
                {isReuse ? (
                  // "Reuse Consultation Files" → always visible, disabled when no files
                  <Button
                    title="Reuse Consultation Files"
                    variant="secondary"
                    size="md"
                    textClassName="text-xs sm:text-sm"
                    className="w-full py-2 px-4 rounded-lg border-2"
                    onClick={handleTogglePreviousFiles}
                    disabled={disabledAction || specialistRecentFiles.length === 0}
                  />
                ) : (
                  // "Select Previous Files" → hidden when no files
                  recentCases.length > 0 && (
                    <Button
                      title="Select Previous Files"
                      variant="secondary"
                      size="md"
                      textClassName="text-xs sm:text-sm"
                      className="w-full py-2 px-4 rounded-lg border-2"
                      onClick={handleTogglePreviousFiles}
                      disabled={disabledAction}
                    />
                  )
                )}
                <Button
                  title="Upload Files"
                  variant="primary"
                  size="md"
                  textClassName="text-xs sm:text-sm"
                  className={cn(
                    "py-2 px-4 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200",
                    (!isReuse && recentCases.length === 0) ? "w-full sm:w-auto sm:min-w-[200px]" : "w-full"
                  )}
                  onClick={openFilePicker}
                  disabled={disabledAction}
                />
              </div>
            );
          })()}
        </div>

        {/* File List */}
        <div className={cn('space-y-3', visibleFiles.length > 0 && 'mt-4')}>
          {visibleFiles.map(file => (
            <FilePreview
              bgVariant="blue"
              allowed_actions={['delete']}
              disabled={disabledAction}
              key={file.id}
              file={file}
              handleDelete={() => handleDelete(file.id)}
              handleView={handleView}
              loading={typeof addReportLoading[file.id] === 'boolean'}
            // error={(isEmpty(invalidFileNames[file.name]) || Boolean(invalidFileNames[file.name]))}
            />
          ))}
        </div>
        <div className={cn('space-y-3', !isEmpty(addReportLoading) && 'mt-4')}>
          {Object.entries(addReportLoading).map(([key, value]) => {
            // only render when loading state has skeleton loading content
            if (typeof value === 'boolean') return;
            return (
              <FilePreview
                bgVariant="blue"
                allowed_actions={['delete']}
                disabled={disabledAction}
                key={key}
                file={value}
                handleDelete={() => handleDelete(value.id)}
                handleView={handleView}
                loading={true}
              />
            );
          })}
        </div>

        {/* Previously Shared Files (Referral case only) */}
        {selectedPreviousFiles.length > 0 && (
          <div>
            <Text
              variant="small"
              className="text-uppercase inline-flex gap-1 items-center cursor-pointer text-ui_input_border_dark text-sm font-FigtreeSemiBold my-2"
              onClick={() => setPreviousFileOpen(!previousFileOpen)}>
              PREVIOUSLY SHARED FILES ({selectedPreviousFiles.length}) {previousFileOpen ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
            </Text>
            <div className="flex flex-col gap-2">
              {previousFileOpen &&
                selectedPreviousFiles.map(file => (
                  <FilePreview key={file.id} file={file as FileSerializableItem} handleView={handleView} allowed_actions={[]} readonly />
                ))}
            </div>
          </div>
        )}

        {/* patient query */}
        <div className="flex flex-col gap-3 items-center mt-6">
          <Text variant="title" className="text-ui_blue text-center">Ask Your Question to the Doctor</Text>
          <Text variant="small" className="text-ui_input_border_dark !font-FigtreeRegular text-center">Your question will be shared with the relevant specialist for review.</Text>
          <div className={cn("rounded-lg self-stretch min-h-[43px] flex flex-col md:flex-row", isPatientQueryEditMode ? "border border-ui_input_border" : "gap-2")}>
            {isPatientQueryEditMode ? (
              <TextInput
                value={patientQueryInput}
                onChangeText={setPatientQueryInput}
                placeholder="Write your medical concern here..."
                className="w-full h-full"
                wrapperClassName="border-none !p-0"
                inputClassName="h-fullrounded outline-none text-sm font-FigtreeRegular w-full overflow-hidden"
                max={2000}
                error={error}
                multiline={width < screenSize.md}
                disabled={patientQueryEditingDisabled}
                preventTrailingSpace
                preventLeadingSpace
              />
            ) : (
              <div className="flex items-center gap-2 grow bg-ui_sky_blue rounded-md p-3">
                <Text variant="small" className="text-ui_blue !font-FigtreeRegular break-all">
                  {patientQuery || patientQueryInput || ' '}
                </Text>
              </div>
            )}
            {(patientQueryInput) && (!patientQueryEditingDisabled) && (<Button
              title={isPatientQueryEditMode ? "Done" : "Edit"}
              variant="secondary"
              size="sm"
              iconClassName="size-3 md:size-4"
              leftIcon={isPatientQueryEditMode ? 'tick' : 'edit'}
              className={cn("border md:self-center self-end mr-[5px]")}
              onClick={isPatientQueryEditMode ? handleSubmitPatientQuery : () => setIsPatientQueryEditing(true)}
            />)}
          </div>
            {error && (
              <span className='flex gap-1 self-start items-center justify-end mr-1 mb-1'>
                <Info className='rotate-180 size-3.5 text-rejection'/>
                <Text variant="small" className="text-rejection self-end">
                  {error}
                </Text>
              </span>
            )}
        </div>

        {/* Next Button */}
        {accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && (
          <>
            {typeof isLoading === 'number' && isLoading !== ProgressBarSteps.NotStarted && currentStep === 1 ? (
              <div className="mt-4 flex justify-center w-full max-w-[600px] mx-auto">
                <ProgressBar currentStep={isLoading} onComplete={handleProgressBarComplete} message='Please bear with us while the uploaded documents are being analyzed as part of the AI categorization process. Thank you for your patience.' />
              </div>
            ) : (
              <Button
                title="Next"
                variant="primary"
                size="lg"
                isLoading={typeof isLoading === 'number' && isLoading === ProgressBarSteps.NotStarted}
                onClick={handleSubmit}
                disabled={
                  (reportAnalyzing) ||
                  !isEmpty(addReportLoading) ||
                  (files && files.length === 0) ||
                  currentStep > 1 ||
                  disabledAction ||
                  (patientQueryInput !== patientQuery && patientQueryInput.trim() !== '')
                }
                textClassName="text-lg"
                className="rounded-md mt-4 self-center shadow-md hover:shadow-lg transition-shadow duration-200 py-1 px-2 w-44"
              />
            )}
          </>
        )}
      </div>

      {/* Category Mismatch Modal */}
      <CategoryMismatchModal
        open={showCategoryMismatch}
        onClose={handleCloseMismatchModal}
        onProceed={handleProceedWithAI}
        selectedCategory={selectedSpecialistName}
        detectedCategory={aiCategoryName}
      />

      {/* Duplicate Files Modal */}
      <DuplicateFilesModal open={showDuplicateModal} onClose={() => setShowDuplicateModal(false)} duplicateFileNames={duplicateFileNames} />

      {/* Invalid Files Modal */}
      <InvalidFilesModal
        open={showInvalidModal}
        onClose={() => setShowInvalidModal(false)}
        invalidFileNames={Object.keys(invalidFileNames)}
      />
      <RecentCasesModal
        open={showRecentCasesModal}
        onClose={() => setShowRecentCasesModal(false)}
        onAddSelectedDocuments={handleAddSelectedPreviousFiles}
        mode={(
          consultationFlow === ConsulationFlowType.PreSelectedSpecialist||
          consultationFlow === ConsulationFlowType.HospitalSpecialistPreSelected || 
          consultation_type === ConsultationType['Follow Up Second Opinion'] || 
          consultation_type === ConsultationType['Referral Second Opinion'] || 
          consultation_type === ConsultationType['Hospital Follow Up Second Opinion'] || 
          consultation_type === ConsultationType['Hospital Referral Second Opinion']
        ) ? 'reuse' : 'select'}
        reuseCases={(
          consultationFlow === ConsulationFlowType.PreSelectedSpecialist || 
          consultationFlow === ConsulationFlowType.HospitalSpecialistPreSelected || 
          consultation_type === ConsultationType['Follow Up Second Opinion'] || 
          consultation_type === ConsultationType['Referral Second Opinion'] || 
          consultation_type === ConsultationType['Hospital Follow Up Second Opinion'] || 
          consultation_type === ConsultationType['Hospital Referral Second Opinion']
        ) ? specialistRecentFiles : undefined}
        specialization={aiRecommendedSpecialists[0]?.specialization?.label}
        alreadyAddedFileIds={alreadyAddedFileIds}
      />
    </AccordianLayout >
  );
};

const WarningMessage: React.FC<React.PropsWithChildren> = props => {
  const { children } = props;
  return (
    <div className="bg-white border border-ui_input_border_dark/65 rounded-lg p-4 flex gap-3 mt-6 border-l-4 border-l-ui_input_border_dark/65 ">
      <Icon type="warning" className="size-5 flex-shrink-0 mt-0.5" />
      <p className="text-base font-FigtreeMedium text-ui_input_border_dark">{children}</p>
    </div>
  );
};
